import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';

const LocationDropdown = ({ value, onChange, className = "" }) => {
  const [locations, setLocations] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Load locations on mount
  useEffect(() => {
    loadLocations();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsAddingLocation(false);
        setNewLocationName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when adding location
  useEffect(() => {
    if (isAddingLocation && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddingLocation]);

  const loadLocations = async () => {
    try {
      const response = await axios.get('/api/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleLocationSelect = (location) => {
    onChange(location);
    setIsOpen(false);
  };

  const handleAddLocationClick = () => {
    setIsAddingLocation(true);
    setNewLocationName('');
  };

  const handleSaveLocation = async () => {
    if (!newLocationName.trim()) return;

    try {
      setLoading(true);
      const response = await axios.post('/api/locations', {
        name: newLocationName.trim()
      });
      
      // Add new location to list
      const newLocation = response.data;
      setLocations(prev => [...prev, newLocation]);
      
      // Select the new location
      onChange(newLocation.name);
      
      // Reset form
      setIsAddingLocation(false);
      setNewLocationName('');
      setIsOpen(false);
    } catch (error) {
      console.error('Error creating location:', error);
      alert('Failed to create location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveLocation();
    } else if (e.key === 'Escape') {
      setIsAddingLocation(false);
      setNewLocationName('');
    }
  };

  const displayValue = value || '-';

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:bg-gray-50 flex items-center justify-between"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {displayValue}
        </span>
        <ChevronDownIcon 
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {/* Default "-" option */}
          <button
            type="button"
            onClick={() => handleLocationSelect('')}
            className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100"
          >
            -
          </button>

          {/* Existing locations */}
          {locations.map((location) => (
            <button
              key={location.id}
              type="button"
              onClick={() => handleLocationSelect(location.name)}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
            >
              {location.name}
            </button>
          ))}

          {/* Add Location Section */}
          {isAddingLocation ? (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={newLocationName}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Enter location name"
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={handleSaveLocation}
                  disabled={!newLocationName.trim() || loading}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingLocation(false);
                    setNewLocationName('');
                  }}
                  className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleAddLocationClick}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 border-t border-gray-200 text-blue-600 flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Location
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationDropdown;